import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import './themes/modern.css';
import './themes/minimal.css';
import './themes/dark.css';
import './styles/global-toast.css';
import UserProfile from './components/UserProfile';
import ProjectEdit from './components/ProjectEdit';
import Settings from './components/Settings';
import Login from './components/Login';
import Register from './components/Register';
import ProjectSelectorV2 from './components/ProjectSelectorV2';
import AddProjectPage from './components/AddProjectPage';
import SchedulePage from './components/SchedulePage';
import AnalysisChat from './components/AnalysisChat';
import PushNotification from './components/PushNotification';
import SimpleScheduledPush from './components/SimpleScheduledPush';
import WebSocketScheduledPush from './components/WebSocketScheduledPush';
import { buildApiUrl } from './config/api';
import ActivityCalendar from './components/ActivityCalendar';
import { pushNotificationService } from './utils/pushNotification';
import { websocketClient } from './utils/websocketClient';

interface UserProject {
  id: number;  // 这个实际上是project_id
  user_project_id: number;
  project_id: number;
  percentage: number;
  hours: number;
  target_hours: number;
  change_percentage: number;
  change_type: string;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ProjectInfo {
  id: number;
  name: string;
  category: string;
  description: string;
  icon: string;
  target_hours: number;
}

function App() {
  const [activeTab, setActiveTab] = useState<'home' | 'add' | 'profile' | 'analysis' | 'schedule' | 'push'>('home');
  const [userProjects, setUserProjects] = useState<UserProject[]>([]);
  const [availableProjects, setAvailableProjects] = useState<ProjectInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('modern');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [activeTimer, setActiveTimer] = useState<number | null>(null);
  
  // 新增：项目编辑页面状态
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  
  // 新增：项目选择器状态
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  
  // 认证相关状态
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  // 新增：记录每个项目的计时秒数
  const [timerSeconds, setTimerSeconds] = useState<{ [projectId: number]: number }>({});
  const timerIntervalRef = useRef<{ [projectId: number]: NodeJS.Timeout | null }>({});
  const fetchingUserInfoRef = useRef(false); // 防止重复请求用户信息
  const fetchingProjectsRef = useRef(false); // 防止重复请求项目列表
  const fetchingAvailableProjectsRef = useRef(false); // 防止重复请求可用项目
  const deletingProjectRef = useRef(false); // 防止重复删除项目

  // ========== 计时器状态管理 ==========
  const [timingProjectId, setTimingProjectId] = useState<number | null>(null);

  // ========== 极简滑动状态 ==========
  const [slidingId, setSlidingId] = useState<number | null>(null);
  const [slidingX, setSlidingX] = useState<number>(0);
  const [swipedId, setSwipedId] = useState<number | null>(null);
  const dragStartX = useRef<number>(0);

  // 触摸事件
  const handleTouchStart = (id: number, e: React.TouchEvent) => {
    if (swipedId !== null && swipedId !== id) setSwipedId(null);
    setSlidingId(id);
    setSlidingX(0);
    dragStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (id: number, e: React.TouchEvent) => {
    if (slidingId !== id) return;
    let deltaX = e.touches[0].clientX - dragStartX.current;
    // 移除 preventDefault() 调用，因为触摸事件默认是被动的
    if (deltaX < MAX_SWIPE) deltaX = MAX_SWIPE + (deltaX - MAX_SWIPE) * DAMPING;
    setSlidingX(Math.min(0, deltaX));
  };
  const handleTouchEnd = (id: number) => {
    if (slidingId !== id) return;
    if (slidingX < MAX_SWIPE / 2) {
      setSwipedId(id);
      setSlidingX(MAX_SWIPE);
    } else {
      setSwipedId(null);
      setSlidingX(0);
    }
    setSlidingId(null);
  };
  // 鼠标事件
  const handleMouseDown = (id: number, e: React.MouseEvent) => {
    if (swipedId !== null && swipedId !== id) setSwipedId(null);
    setSlidingId(id);
    setSlidingX(0);
    dragStartX.current = e.clientX;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };
  const onMouseMove = (e: MouseEvent) => {
    if (slidingId === null) return;
    let deltaX = e.clientX - dragStartX.current;
    if (deltaX < MAX_SWIPE) deltaX = MAX_SWIPE + (deltaX - MAX_SWIPE) * DAMPING;
    setSlidingX(Math.min(0, deltaX));
  };
  const onMouseUp = () => {
    if (slidingId === null) return;
    if (slidingX < MAX_SWIPE / 2) {
      setSwipedId(slidingId);
      setSlidingX(MAX_SWIPE);
    } else {
      setSwipedId(null);
      setSlidingX(0);
    }
    setSlidingId(null);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  };

  // 检查本地存储的认证信息
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedRefreshToken = localStorage.getItem('refreshToken');
    const savedUser = localStorage.getItem('user');
    
    console.log('检查本地存储的认证信息:');
    console.log('保存的token:', savedToken ? savedToken.substring(0, 20) + '...' : 'null');
    console.log('保存的refreshToken:', savedRefreshToken ? savedRefreshToken.substring(0, 20) + '...' : 'null');
    console.log('保存的用户:', savedUser);
    
    if (savedToken && savedRefreshToken && savedUser) {
      const userData = JSON.parse(savedUser);
      setToken(savedToken);
      setUser(userData);
      setIsAuthenticated(true);
      console.log('认证状态已恢复');
      
      // 恢复WebSocket连接
      websocketClient.connect(userData.id.toString()).then(() => {
        console.log('WebSocket连接已恢复');
      }).catch(error => {
        console.error('WebSocket连接恢复失败:', error);
      });
    } else {
      console.log('本地没有有效的认证信息');
      // 确保状态一致
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // 初始化推送通知服务
  useEffect(() => {
    if (isAuthenticated && pushNotificationService.isSupported()) {
      pushNotificationService.registerServiceWorker().catch(error => {
        console.warn('推送通知服务初始化失败:', error);
      });
    }
  }, [isAuthenticated]);

  // 刷新令牌函数
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.log('没有刷新令牌，无法刷新访问令牌');
      return false;
    }

    try {
      console.log('尝试刷新访问令牌...');
      const response = await fetch(buildApiUrl('/api/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('令牌刷新成功');
        
        // 更新本地存储和状态
        localStorage.setItem('authToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        setToken(data.access_token);
        
        return true;
      } else {
        console.log('令牌刷新失败:', response.status);
        // 刷新失败时清除本地存储
        handleLogout();
        return false;
      }
    } catch (error) {
      console.error('刷新令牌时发生错误:', error);
      // 发生错误时也清除本地存储
      handleLogout();
      return false;
    }
  }, []); // 空依赖数组，确保函数引用稳定

  // 带认证的API请求函数
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('发送请求到1:', buildApiUrl(url));
      console.log('使用令牌:', token.substring(0, 20) + '...');
      console.log('完整Authorization头部:', headers['Authorization'].substring(0, 30) + '...');
    } else {
      console.log('发送请求到2:', buildApiUrl(url), '无令牌');
      console.log('当前token状态:', token);
      console.log('localStorage token:', localStorage.getItem('authToken'));
    }

    const response = await fetch(buildApiUrl(url), {
      ...options,
      headers,
    });

    // 如果返回401，尝试刷新令牌
    if (response.status === 401) {
      console.log('收到401错误，尝试刷新令牌');
      const refreshSuccess = await refreshAccessToken();
      
      if (refreshSuccess) {
        // 使用新令牌重试请求
        const newToken = localStorage.getItem('authToken');
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          console.log('使用新令牌重试请求');
          return await fetch(buildApiUrl(url), {
            ...options,
            headers,
          });
        }
      } else {
        // 刷新失败，清除本地存储的令牌
        console.log('令牌刷新失败，清除本地令牌');
        handleLogout();
        // 返回原始响应，让调用者知道认证失败
        return response;
      }
    }

    return response;
  }, [token, refreshAccessToken]);

  // 检查是否有正在进行的计时活动
  const checkCurrentActivity = useCallback(async () => {
    try {
      console.log('开始检查当前活动状态...');
      const response = await authenticatedFetch('/api/me/activities/current');
      
      // 检查响应状态
      if (response.status === 401) {
        console.log('检查当前活动时收到401错误，用户未认证');
        setActiveTimer(null);
        setTimingProjectId(null);
        return;
      }
      
      if (!response.ok) {
        console.error('检查当前活动失败:', response.status);
        setActiveTimer(null);
        setTimingProjectId(null);
        return;
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setActiveTimer(result.data.project_id);
        setTimingProjectId(result.data.project_id);
        // 设置初始计时秒数
        const elapsedSeconds = Math.floor(result.data.elapsed_minutes * 60);
        setTimerSeconds(prev => ({
          ...prev,
          [result.data.project_id]: elapsedSeconds
        }));
        console.log('发现正在进行的活动:', result.data.project_id);
      } else {
        setActiveTimer(null);
        setTimingProjectId(null);
        console.log('没有正在进行的活动');
      }
    } catch (error) {
      console.error('检查当前活动失败:', error);
      setActiveTimer(null);
      setTimingProjectId(null);
    }
  }, [authenticatedFetch]);

  // 检查当前活动状态
  useEffect(() => {
    if (isAuthenticated && token) {
      // 延迟一点时间确保认证状态完全恢复
      const timer = setTimeout(() => {
        checkCurrentActivity();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, token, checkCurrentActivity]);

  // 处理登录成功
  const handleLogin = async (accessToken: string, refreshToken: string, userData: any) => {
    setToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // 建立WebSocket连接
    try {
      await websocketClient.connect(userData.id.toString());
      console.log('WebSocket连接已建立');
    } catch (error) {
      console.error('WebSocket连接失败:', error);
    }
  };

  // 处理注册成功
  const handleRegister = async (accessToken: string, refreshToken: string, userData: any) => {
    setToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // 建立WebSocket连接
    try {
      await websocketClient.connect(userData.id.toString());
      console.log('WebSocket连接已建立');
    } catch (error) {
      console.error('WebSocket连接失败:', error);
    }
  };

  // 处理登出
  const handleLogout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // 断开WebSocket连接
    websocketClient.disconnect();
    console.log('WebSocket连接已断开');
  };

  // 切换登录/注册界面
  const switchToRegister = () => setShowLogin(false);
  const switchToLogin = () => setShowLogin(true);

  // 获取用户已添加的项目
  const fetchUserProjects = useCallback(async () => {
    // 防止重复请求
    if (fetchingProjectsRef.current) {
      return;
    }
    fetchingProjectsRef.current = true;
    
    setLoading(true);
    try {
      const res = await authenticatedFetch('/api/me/projects/selected');
      const data = await res.json();
      setUserProjects(data);
    } catch (error) {
      console.error('获取用户项目失败:', error);
    } finally {
      setLoading(false);
      fetchingProjectsRef.current = false;
    }
  }, [authenticatedFetch]);

  // 获取可添加的项目列表
  const fetchAvailableProjects = useCallback(async () => {
    // 防止重复请求
    if (fetchingAvailableProjectsRef.current) {
      return;
    }
    fetchingAvailableProjectsRef.current = true;
    
    try {
      const response = await authenticatedFetch('/api/me/projects/available');
      const result = await response.json();
      // 后端直接返回项目列表，不是 {success: true, data: [...]} 格式
      if (Array.isArray(result)) {
        // 过滤掉已添加的项目
        const userProjectIds = userProjects.map(up => up.id); // 使用id字段，它实际上是project_id
        const availableProjects = result.filter((project: ProjectInfo) => 
          !userProjectIds.includes(project.id)
        );
        setAvailableProjects(availableProjects);
      }
    } catch (error) {
      console.error('获取可添加项目失败:', error);
    } finally {
      fetchingAvailableProjectsRef.current = false;
    }
  }, [authenticatedFetch, userProjects]);

  // 获取可添加的项目列表（不依赖userProjects，避免循环依赖）
  const fetchAvailableProjectsSimple = useCallback(async () => {
    // 防止重复请求
    if (fetchingAvailableProjectsRef.current) {
      return;
    }
    fetchingAvailableProjectsRef.current = true;
    
    try {
      const response = await authenticatedFetch('/api/me/projects/available');
      const result = await response.json();
      // 后端直接返回项目列表，不是 {success: true, data: [...]} 格式
      if (Array.isArray(result)) {
        // 不过滤，直接设置所有可用项目
        setAvailableProjects(result);
      }
    } catch (error) {
      console.error('获取可添加项目失败:', error);
    } finally {
      fetchingAvailableProjectsRef.current = false;
    }
  }, [authenticatedFetch]);

  // 获取用户信息
  const fetchUserInfo = useCallback(async () => {
    // 防止重复请求
    if (fetchingUserInfoRef.current) {
      return;
    }
    fetchingUserInfoRef.current = true;
    
    try {
      const response = await authenticatedFetch('/api/me');
      const data = await response.json();
      setUser(data);
    } catch (error) {
      console.error('获取用户信息失败:', error);
    } finally {
      fetchingUserInfoRef.current = false;
    }
  }, [authenticatedFetch]);

  const fetchAllProjects = useCallback(async () => {
    // 防止重复请求
    if (fetchingAvailableProjectsRef.current) {
      return;
    }
    fetchingAvailableProjectsRef.current = true;
    
    try {
      const response = await authenticatedFetch('/api/me/projects/available');
      const result = await response.json();
      // 后端直接返回项目列表，不是 {success: true, data: [...]} 格式
      if (Array.isArray(result)) {
        setAvailableProjects(result); // 不过滤，全部存
      }
    } catch (error) {
      console.error('获取全部项目失败:', error);
    } finally {
      fetchingAvailableProjectsRef.current = false;
    }
  }, [authenticatedFetch]);

  // 初始化时拉取所有项目，保证首页渲染正常
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchAllProjects();
    }
  }, [isAuthenticated, token, fetchAllProjects]);

  // 当切换到首页时获取用户项目
  useEffect(() => {
    if (activeTab === 'home' && isAuthenticated && token) {
      // 确保先加载所有项目信息，再加载用户项目
      fetchAllProjects().then(() => {
        fetchUserProjects();
        checkCurrentActivity(); // 检查是否有正在进行的活动
      });
    }
  }, [activeTab, isAuthenticated, token, fetchAllProjects, fetchUserProjects, checkCurrentActivity]);

  // 修正 useEffect 死循环问题
  useEffect(() => {
    if (activeTab === 'add' && isAuthenticated && token) {
      fetchUserProjects(); // 只获取用户项目，不获取可用项目
    }
  }, [activeTab, isAuthenticated, token, fetchUserProjects]);

  // 初始化时获取用户信息
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUserInfo();
    }
  }, [isAuthenticated, token, fetchUserInfo]);

  // 计时器启动时，开始本地计时
  useEffect(() => {
    const currentTimerRef = timerIntervalRef.current;
    userProjects.forEach((userProject) => {
      if (activeTimer === userProject.id) {
        if (!currentTimerRef[userProject.id]) {
          currentTimerRef[userProject.id] = setInterval(() => {
            setTimerSeconds(prev => ({
              ...prev,
              [userProject.id]: (prev[userProject.id] || 0) + 1
            }));
          }, 1000);
        }
      } else {
        if (currentTimerRef[userProject.id]) {
          clearInterval(currentTimerRef[userProject.id]!);
          currentTimerRef[userProject.id] = null;
        }
        setTimerSeconds(prev => ({ ...prev, [userProject.id]: 0 }));
      }
    });
    return () => {
      Object.values(currentTimerRef).forEach(timer => timer && clearInterval(timer));
    };
  }, [activeTimer, userProjects]);

  const getProjectInfo = (projectId: number): ProjectInfo | null => {
    return availableProjects.find(p => p.id === projectId) || null;
  };

  // 新增：计算正确的percentage
  const calculatePercentage = (hours: number, targetHours: number): number => {
    if (targetHours <= 0) return 0;
    const percentage = (hours / targetHours) * 100;
    return percentage; // 不限制最大值，允许超过100%
  };

  // 新增：处理项目点击进入编辑页面
  const handleProjectClick = (userProjectId: number) => {
    setEditingProjectId(userProjectId);
  };

  // 新增：处理从编辑页面返回
  const handleBackFromEdit = () => {
    setEditingProjectId(null);
    // 同时刷新用户项目列表和项目基本信息，确保数据同步
    fetchAllProjects().then(() => {
      fetchUserProjects();
    });
  };

  const handleThemeChange = (theme: string) => {
    setCurrentTheme(theme);
    // 可以在这里保存到localStorage
    localStorage.setItem('selectedTheme', theme);
  };

  const handleSettingsToggle = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  // 计时器相关函数
  // 获取地理位置的辅助函数
  const getCurrentPosition = (): Promise<{lng: number|null, lat: number|null}> => {
    return new Promise(resolve => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => resolve({ lng: pos.coords.longitude, lat: pos.coords.latitude }),
          () => resolve({ lng: null, lat: null }),
          { enableHighAccuracy: true, timeout: 3000, maximumAge: 0 }
        );
      } else {
        resolve({ lng: null, lat: null });
      }
    });
  };

  const handleStartTimer = async (projectId: number) => {
    try {
      // 如果当前有正在计时的项目，先结束它
      if (activeTimer && activeTimer !== projectId) {
        console.log(`先结束项目 ${activeTimer} 的计时，再开始项目 ${projectId} 的计时`);
        await handleStopTimer(activeTimer, false); // 不重置滑动状态，因为是内部切换
      }
      const { lng, lat } = await getCurrentPosition();
      const res = await authenticatedFetch('/api/me/activities/start', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, lng, lat })
      });
      
      if (res.ok) {
        setActiveTimer(projectId);
        setTimingProjectId(projectId);
        // 回到初始状态：关闭滑动状态
        setSwipedId(null);
        setSlidingX(0);
        setSlidingId(null);
        console.log('活动开始记录成功');
      } else {
        console.error('开始活动失败');
      }
    } catch (error) {
      console.error('开始活动失败:', error);
    }
  };

  const handleStopTimer = async (projectId: number, resetSwipeState: boolean = true) => {
    try {
      const { lng, lat } = await getCurrentPosition();
      const res = await authenticatedFetch('/api/me/activities/end', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, lng, lat })
      });
      
      if (res.ok) {
        setActiveTimer(null);
        setTimingProjectId(null); // 重置计时项目ID，确保头像变回原图标
        // 只有在用户主动点击结束计时时才重置滑动状态
        if (resetSwipeState) {
          setSwipedId(null);
          setSlidingX(0);
          setSlidingId(null);
        }
        // 刷新项目数据以更新小时数
        await fetchUserProjects();
        console.log('活动结束记录成功');
      } else {
        console.error('停止计时失败');
      }
    } catch (error) {
      console.error('停止计时失败:', error);
    }
  };

  const handleTimerToggle = (projectId: number) => {
    if (activeTimer === projectId) {
      // 用户主动点击结束计时，重置滑动状态
      handleStopTimer(projectId, true);
    } else {
      handleStartTimer(projectId);
    }
  };

  // 初始化时从localStorage读取主题
  useEffect(() => {
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // ========== 微信风格滑动参数 ==========
  const DAMPING = 0.3;

  // 计算按钮宽度的函数
  const calculateButtonWidth = (text: string, baseWidth: number = 60) => {
    // 根据文字长度计算合适的宽度，确保文字能完整显示
    const textLength = text.length;
    if (textLength <= 2) return baseWidth;
    if (textLength <= 4) return baseWidth + 20;
    if (textLength <= 6) return baseWidth + 40;
    return baseWidth + 60; // 最大宽度，确保长文字能完整显示
  };

  // 按钮配置
  const buttonConfigs = [
    { 
      text: (userProject: UserProject) => activeTimer === userProject.id ? '结束计时' : '开始计时', 
      color: (userProject: UserProject) => activeTimer === userProject.id ? '#ff4d4f' : '#52c41a', 
      action: (userProject: UserProject) => handleTimerToggle(userProject.id) 
    },
    { 
      text: (userProject: UserProject) => '不显示', 
      color: (userProject: UserProject) => '#1677ff', 
      action: (userProject: UserProject) => alert('不显示') 
    },
    { 
      text: (userProject: UserProject) => '删除', 
      color: (userProject: UserProject) => '#ff4d4f', 
      action: (userProject: UserProject) => handleDeleteProject(userProject) 
    }
  ];

  // 计算总按钮宽度
  const totalButtonWidth = buttonConfigs.reduce((sum, config) => {
    const text = config.text({} as UserProject);
    return sum + calculateButtonWidth(text);
  }, 0);
  
  // 动态计算最大滑动距离
  const MAX_SWIPE = -totalButtonWidth;

  // ========== 删除项目方法 ==========
  const handleDeleteProject = async (userProject: UserProject) => {
    if (!window.confirm('确定要删除该项目吗？')) return;
    
    // 防止重复删除
    if (deletingProjectRef.current) {
      return;
    }
    deletingProjectRef.current = true;
    
    try {
      const res = await authenticatedFetch(`/api/me/projects/${userProject.project_id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        await fetchUserProjects();
        setSwipedId(null);
        setSlidingX(0);
      } else {
        alert('删除失败，请重试');
      }
    } catch (error) {
      alert('网络错误，请重试');
    } finally {
      deletingProjectRef.current = false;
    }
  };

  const renderMainContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="main-content">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>加载中...</p>
              </div>
            ) : userProjects.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h3>暂无项目</h3>
                <p>开始你的时间管理之旅，点击下方 + 按钮添加第一个项目吧！</p>
                <button 
                  className="add-first-project-btn"
                  onClick={() => setActiveTab('add')}
                >
                  + 添加第一个项目
                </button>
              </div>
            ) : availableProjects.length === 0 ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>加载项目信息中...</p>
              </div>
            ) : (
              <div className="home-project-cards" style={{ 
                width: '100%', 
                maxWidth: '100%', 
                margin: 0, 
                padding: 0, 
                boxSizing: 'border-box', 
                overflow: 'hidden'
              }}>
                {userProjects
                  .map((userProject) => {
                    const projectInfo = getProjectInfo(userProject.id);
                    if (!projectInfo) return null;
                    const correctPercentage = calculatePercentage(userProject.hours, userProject.target_hours);
                    return { userProject, projectInfo, correctPercentage };
                  })
                  .filter(item => item !== null)
                  .sort((a, b) => b!.correctPercentage - a!.correctPercentage) // 按百分比从大到小排序
                                    .map((item) => {
                    const { userProject, projectInfo, correctPercentage } = item!;
                    const isSliding = slidingId === userProject.id;
                    const isSwiped = swipedId === userProject.id;
                  
                  return (
                    <div
                      key={userProject.user_project_id}
                      className="swipe-action"
                      style={{ 
                        position: 'relative', 
                        overflow: 'hidden', 
                        width: '100%', 
                        background: 'transparent', 
                        margin: 0, 
                        padding: 0 
                      }}
                    >
                      {/* 操作按钮组 - 手风琴展开效果（从右向左） */}
                      <div 
                        className="swipe-action-actions"
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          height: '100%',
                          display: 'flex',
                          width: `${totalButtonWidth}px`,
                          zIndex: 1,
                          pointerEvents: isSwiped ? 'auto' : 'none',
                          transition: 'pointer-events 0.2s',
                          // 手风琴展开效果：从右侧开始展开
                          overflow: 'hidden',
                          flexDirection: 'row-reverse', // 从右向左排列
                          // 确保与聊天窗口右侧边缘完美对齐
                          margin: 0,
                          padding: 0,
                          border: 'none',
                          outline: 'none',
                          // 按钮组位置：根据滑动状态动态调整，更靠左避免超出屏幕
                          transform: `translateX(${totalButtonWidth + (window.innerWidth <= 600 ? 8 : 16) - 30 + (isSliding ? slidingX : isSwiped ? MAX_SWIPE : 0)}px)`,
                        }}
                      >
                        <button
                          style={{
                            width: `${Math.max(0, Math.min(calculateButtonWidth(buttonConfigs[0].text(userProject)), Math.abs(isSliding ? slidingX : isSwiped ? MAX_SWIPE : 0) * (calculateButtonWidth(buttonConfigs[0].text(userProject)) / totalButtonWidth)))}px`,
                            background: buttonConfigs[0].color(userProject),
                            color: 'white',
                            border: 'none',
                            fontWeight: 500,
                            fontSize: '15px',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            // 手风琴展开：从右侧开始
                            minWidth: 0,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            padding: '0 8px',
                            textAlign: 'center',
                          }}
                          onClick={e => { e.stopPropagation(); buttonConfigs[0].action(userProject); }}
                          disabled={!isSwiped}
                        >{buttonConfigs[0].text(userProject)}</button>
                        <button
                          style={{
                            width: `${Math.max(0, Math.min(calculateButtonWidth(buttonConfigs[1].text(userProject)), Math.abs(isSliding ? slidingX : isSwiped ? MAX_SWIPE : 0) * (calculateButtonWidth(buttonConfigs[1].text(userProject)) / totalButtonWidth)))}px`,
                            background: buttonConfigs[1].color(userProject),
                            color: 'white',
                            border: 'none',
                            fontWeight: 500,
                            fontSize: '15px',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            // 手风琴展开：稍微延迟
                            minWidth: 0,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            padding: '0 8px',
                            textAlign: 'center',
                          }}
                          onClick={e => { e.stopPropagation(); buttonConfigs[1].action(userProject); }}
                          disabled={!isSwiped}
                        >{buttonConfigs[1].text(userProject)}</button>
                        <button
                          style={{
                            width: `${Math.max(0, Math.min(calculateButtonWidth(buttonConfigs[2].text(userProject)), Math.abs(isSliding ? slidingX : isSwiped ? MAX_SWIPE : 0) * (calculateButtonWidth(buttonConfigs[2].text(userProject)) / totalButtonWidth)))}px`,
                            background: buttonConfigs[2].color(userProject),
                            color: 'white',
                            border: 'none',
                            fontWeight: 500,
                            fontSize: '15px',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            // 手风琴展开：更多延迟
                            minWidth: 0,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            padding: '0 8px',
                            textAlign: 'center',
                          }}
                          onClick={e => { e.stopPropagation(); buttonConfigs[2].action(userProject); }}
                          disabled={!isSwiped}
                        >{buttonConfigs[2].text(userProject)}</button>
                      </div>
                      
                      {/* 卡片内容 - 基于 Ant Design Mobile */}
                      <div
                        className="swipe-action-content"
                        style={{
                          position: 'relative',
                          width: '100%',
                          transform: `translateX(${isSliding ? slidingX : isSwiped ? MAX_SWIPE : 0}px)`,
                          transition: isSliding ? 'none' : 'transform 0.2s',
                          background: 'transparent',
                          margin: 0,
                          padding: 0,
                          zIndex: 2,
                        }}
                        onTouchStart={e => handleTouchStart(userProject.id, e)}
                        onTouchMove={e => handleTouchMove(userProject.id, e)}
                        onTouchEnd={() => handleTouchEnd(userProject.id)}
                        onMouseDown={e => handleMouseDown(userProject.id, e)}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (swipedId !== null && swipedId !== userProject.id) {
                            setSwipedId(null);
                          } else if (swipedId === null) {
                            // 只有在没有滑动状态时才进入编辑页面
                            handleProjectClick(userProject.user_project_id);
                          }
                        }}
                      >
                        {/* 微信风格卡片结构 */}
                        <div 
                          className="wx-project-card" 
                          style={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            background: 'transparent',
                            margin: 0,
                            padding: 0,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleProjectClick(userProject.user_project_id)}
                        >
                          <div className="wx-card-avatar">
                            {timingProjectId === userProject.id ? (
                              (() => {
                                const totalSec = timerSeconds[userProject.id] || 0;
                                const sec = totalSec % 60;
                                const min = Math.floor(totalSec / 60) % 60;
                                const hour = Math.floor(totalSec / 3600) % 12;
                                // 计算角度
                                const secAngle = sec * 6; // 360/60
                                const minAngle = min * 6 + sec * 0.1; // 360/60 + 秒的偏移
                                const hourAngle = hour * 30 + min * 0.5; // 360/12 + 分钟的偏移
                                return (
                                  <svg width="40" height="40" viewBox="0 0 40 40">
                                    <defs>
                                      <linearGradient id="clockGradient" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0%" stopColor="#1677ff"/>
                                        <stop offset="100%" stopColor="#7f3fff"/>
                                      </linearGradient>
                                    </defs>
                                    <circle cx="20" cy="20" r="18" fill="url(#clockGradient)" stroke="url(#clockGradient)" strokeWidth="1" />
                                    {/* 时针 */}
                                    <line x1="20" y1="20" x2="20" y2="11" stroke="#fff" strokeWidth="2" strokeLinecap="round" transform={`rotate(${hourAngle} 20 20)`} />
                                    {/* 分针 */}
                                    <line x1="20" y1="20" x2="20" y2="7" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" transform={`rotate(${minAngle} 20 20)`} />
                                    {/* 秒针 */}
                                    <line x1="20" y1="20" x2="20" y2="5" stroke="#52c41a" strokeWidth="0.8" strokeLinecap="round" transform={`rotate(${secAngle} 20 20)`} />
                                    {/* 圆心 */}
                                    <circle cx="20" cy="20" r="1.5" fill="#fff" />
                                  </svg>
                                );
                              })()
                            ) : (
                              projectInfo.icon
                            )}
                          </div>
                          <div className="wx-card-main">
                            <div className="wx-card-title-row">
                              <span className="wx-card-title">{projectInfo.name}</span>
                            </div>
                            <div className="wx-card-progress-container">
                              <div className="wx-card-progress-bar">
                                <div className="wx-card-progress-fill" style={{width: `${correctPercentage}%`}} />
                              </div>
                              <span className="wx-card-progress-text">{formatPercent(correctPercentage)}</span>
                            </div>
                            <div className="wx-card-desc">{formatHours(userProject.hours)}h / {formatHours(userProject.target_hours)}h</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      case 'analysis':
        return (
          <AnalysisChat
            userProjects={userProjects}
            availableProjects={availableProjects}
            authenticatedFetch={authenticatedFetch}
            onBack={() => setActiveTab('home')}
            currentTheme={currentTheme}
          />
        );
      case 'add':
        return (
          <AddProjectPage
            authenticatedFetch={authenticatedFetch}
            onProjectAdded={() => {
              fetchUserProjects();
              fetchAvailableProjects();
            }}
            onBack={() => setActiveTab('home')}
          />
        );
      case 'schedule':
        return (
          <SchedulePage 
            authenticatedFetch={authenticatedFetch}
            onScheduleAdded={() => {
              // 可以在这里添加行程添加后的回调逻辑
            }}
            onBack={() => setActiveTab('home')}
          />
        );
      case 'profile':
        return (
          <div className="profile-page">
            <UserProfile 
              user={user} 
              onSettingsClick={handleSettingsToggle} 
              onLogout={handleLogout}
              authenticatedFetch={authenticatedFetch}
              currentTheme={currentTheme}
            />
          </div>
        );
      case 'push':
        return (
          <div className="push-page">
            <div className="page-header">
              <button className="back-button" onClick={() => setActiveTab('home')}>
                ← 返回
              </button>
              <h2>推送通知</h2>
            </div>
            <PushNotification currentTheme={currentTheme} />
            <SimpleScheduledPush currentTheme={currentTheme} />
            <WebSocketScheduledPush currentTheme={currentTheme} userId={user?.id?.toString()} />
          </div>
        );
      default:
        return <div>首页</div>;
    }
  };



  // 如果未认证，显示登录注册界面
  if (!isAuthenticated) {
    return (
      <div className="App">
        {showLogin ? (
          <Login onLogin={handleLogin} onSwitchToRegister={switchToRegister} />
        ) : (
          <Register onRegister={handleRegister} onSwitchToLogin={switchToLogin} />
        )}
      </div>
    );
  }

  return (
    <div className={`App theme-${currentTheme}`}>
      {/* 顶部导航栏 - 在ProjectEdit页面时隐藏 */}
      {!editingProjectId && (
        <header className="app-header">
          <div className="header-content">
            <h1 className="app-title">Δelta</h1>
            <div className="header-actions">
              {/* <button className="header-button" onClick={testApiConnection}>调试</button> */}
            </div>
          </div>
        </header>
      )}

      {/* 主要内容区域 */}
      <main className="app-main">
        {editingProjectId ? (
          <ProjectEdit 
            userProjectId={editingProjectId}
            onBack={handleBackFromEdit}
            authenticatedFetch={authenticatedFetch}
            onProjectInfoUpdated={() => {
              // 项目信息更新后刷新数据
              fetchAllProjects().then(() => {
                fetchUserProjects();
              });
            }}
            currentTheme={currentTheme}
          />
        ) : (
          renderMainContent()
        )}
      </main>

      {/* 底部导航栏 - 在ProjectEdit页面和AI分析页面时隐藏 */}
      {!editingProjectId && (
        <nav className="bottom-nav" style={{ display: activeTab === 'analysis' ? 'none' : 'flex' }}>
          <button 
            className={`nav-button ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => setActiveTab('home')}
          >
            首页
          </button>
          <button 
            className={`nav-button ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            DeepSeek
          </button>
          <button 
            className={`nav-button ${activeTab === 'add' ? 'active' : ''} ${activeTab === 'add' ? 'add-button active' : 'add-button'}`}
            onClick={() => setActiveTab('add')}
          >
            +Δ
          </button>
          <button 
            className={`nav-button ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            行程
          </button>
          <button 
            className={`nav-button ${activeTab === 'push' ? 'active' : ''}`}
            onClick={() => setActiveTab('push')}
          >
            推送
          </button>
          <button 
            className={`nav-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            我的
          </button>
        </nav>
      )}

      <Settings 
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        currentTheme={currentTheme}
        onThemeChange={handleThemeChange}
      />
      
      <ProjectSelectorV2
        isOpen={showProjectSelector}
        onClose={() => setShowProjectSelector(false)}
        onProjectAdded={() => {
          fetchUserProjects();
          // 不在这里调用 fetchAvailableProjects，因为 ProjectSelectorV2 内部已经会刷新数据
        }}
        authenticatedFetch={authenticatedFetch}
      />
    </div>
  );
}

// 格式化时长，保留三位有效数字
function formatHours(hours: number) {
  if (isNaN(hours)) return '0.000';
  return hours.toFixed(3);
}
// 格式化百分比，保留一位小数
function formatPercent(percent: number) {
  if (isNaN(percent)) return '0.0%';
  return percent.toFixed(1) + '%';
}

export default App; 